import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import Transactions from 'components/giving/Transactions'
import PaymentMethods from 'components/giving/PaymentMethods'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

export default function GivingSettings() {
    const { t } = useTranslation('giving')
    const location = useLocation()
    const navigate = useNavigate()
    const view = new URLSearchParams(location.search).get('view') || 'transactions'
    const { profileChurch } = useSelector((state) => state.profileChurch)

    useEffect(() => {
        if(!new URLSearchParams(location.search).get('view')){
            navigate('/settings?section=giving&view=transactions')
        }
    }, [location.search, navigate])

    const handleView = (value) => {
        navigate(`/settings?section=giving&view=${value}`)
    }

    if(!profileChurch || !profileChurch._id){
        return (
            <GivingSettingsContainer>
                <HeaderSection>
                    <PageTitle>{t('title')}</PageTitle>
                    <PageSubtitle>{t('settings.description')}</PageSubtitle>
                </HeaderSection>
                <EmptyState>
                    {t('settings.openChurch')}
                </EmptyState>
            </GivingSettingsContainer>
        )
    }

    return (
        <GivingSettingsContainer>
            <HeaderSection>
                <div>
                    <PageTitle>{t('title')}</PageTitle>
                    <PageSubtitle>{t('settings.descriptionFor', { church: profileChurch.fullName })}</PageSubtitle>
                </div>
            </HeaderSection>

            <Tabs>
                <TabButton active={view === 'transactions'} onClick={() => handleView('transactions')}>
                    <ReceiptLongIcon />
                    <span>{t('tabs.transactions')}</span>
                </TabButton>
                <TabButton active={view === 'payment-methods'} onClick={() => handleView('payment-methods')}>
                    <CreditCardIcon />
                    <span>{t('settings.paymentMethods')}</span>
                </TabButton>
            </Tabs>

            <ContentSection>
                {view === 'payment-methods' ? <PaymentMethods /> : <Transactions />}
            </ContentSection>
        </GivingSettingsContainer>
    )
}

const GivingSettingsContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    color: var(--lightThemeFontColor);
    overflow-y: auto;
    padding: 20px;
    gap: 18px;

    @media (max-width: 768px) {
        padding: 12px;
    }
`

const HeaderSection = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
`

const PageTitle = styled.h1`
    margin: 0;
    font-family: 'Roboto', sans-serif;
    font-size: 24px;
    font-weight: 500;
    color: var(--lightThemeFontColor);
`

const PageSubtitle = styled.p`
    margin: 8px 0 0;
    max-width: 560px;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    line-height: 1.45;
    color: var(--dFontColor);
`

const Tabs = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`

const TabButton = styled.button`
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 14px;
    border-radius: 6px;
    border: 1px solid ${props => props.active ? 'var(--themeColor)' : 'var(--borderColor)'};
    background: ${props => props.active ? 'var(--themeColor)' : '#fff'};
    color: ${props => props.active ? '#fff' : 'var(--lightThemeFontColor)'};
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    cursor: pointer;

    svg {
        font-size: 18px;
    }

    &:hover {
        border-color: var(--themeColor);
        background: ${props => props.active ? 'var(--themeColor)' : 'rgba(0, 180, 204, 0.08)'};
    }
`

const ContentSection = styled.div`
    width: 100%;
    min-height: 360px;
`

const EmptyState = styled.div`
    width: 100%;
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
    border: 1px dashed var(--borderColor);
    border-radius: 8px;
    color: var(--dFontColor);
    background: #fff;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
`
